import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import nspell from 'nspell';
import aff from './en-GB.aff?raw';
import dic from './en-GB.dic?raw';

export interface SpellCheckClickData {
  word: string;
  suggestions: string[];
  from: number;
  to: number;
  rect: DOMRect;
}

export interface SpellCheckOptions {
  onSpellCheckClick?: (data: SpellCheckClickData) => void;
}

export const SpellCheckPluginKey = new PluginKey('spellCheck');

export const SpellCheck = Extension.create<SpellCheckOptions>({
  name: 'britishSpellCheck',

  addOptions() {
    return {
      onSpellCheckClick: undefined,
    };
  },

  addProseMirrorPlugins() {
    const spell = nspell(aff, dic);
    const segmenter = new Intl.Segmenter('en-GB', { granularity: 'word' });
    const { onSpellCheckClick } = this.options;

    function buildDecorations(doc: any) {
      const decorations: Decoration[] = [];

      doc.descendants((node: any, pos: number) => {
        if (!node.isText || !node.text) return;

        const text = node.text;
        const segments = segmenter.segment(text);

        for (const segment of segments) {
          if (!segment.isWordLike) continue;

          const word = segment.segment;
          if (/^\d+$/.test(word)) continue;

          if (!spell.correct(word)) {
            const from = pos + segment.index;
            const to = from + word.length;

            decorations.push(
              Decoration.inline(from, to, {
                class: 'spell-error',
                'data-from': from.toString(),
                'data-to': to.toString(),
                'data-word': word,
              })
            );
          }
        }
      });

      return DecorationSet.create(doc, decorations);
    }

    return [
      new Plugin({
        key: SpellCheckPluginKey,
        state: {
          init(_, { doc }) {
            return buildDecorations(doc);
          },
          apply(tr, oldSet, _oldState, newState) {
            if (tr.docChanged) {
              return buildDecorations(newState.doc);
            }
            return oldSet.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
          handleClick(_view, _pos, event) {
            const target = event.target as HTMLElement;

            if (target && target.classList.contains('spell-error')) {
              const word = target.getAttribute('data-word');
              const from = parseInt(target.getAttribute('data-from') || '0', 10);
              const to = parseInt(target.getAttribute('data-to') || '0', 10);

              if (word && onSpellCheckClick) {
                const suggestions = spell.suggest(word);
                const rect = target.getBoundingClientRect();

                onSpellCheckClick({
                  word,
                  suggestions,
                  from,
                  to,
                  rect,
                });
                return true;
              }
            }
            return false;
          },
        },
      }),
    ];
  },
});